import { useState } from "react";
import { Plus } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { v4 } from "uuid";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { CompanySelect } from "@/services/backend/company/components/company-select";

/** The create request every sample base data kind shares. */
export interface CreateSampleBaseDataRequest {
  id: UUID;
  companyId: UUID;
  label: string;
}

/**
 * The create mutation of a sample base data kind, i.e. the return of e.g.
 * `useSamplesAirMicrobialBaseDataCreateMutation()`. Only the members the
 * dialog relies on are described, so the RTK Query tuple is assignable.
 */
export type CreateSampleBaseDataMutation = readonly [
  (request: CreateSampleBaseDataRequest) => { unwrap: () => Promise<unknown> },
  { isLoading: boolean; error?: unknown; reset: () => void },
];

const emptyForm = { label: "", companyId: "" };

/**
 * Creates a sample base data instance of the kind the mutation belongs to.
 * The kind specific dialogs pass their mutation, see e.g.
 * `CreateAirMicrobialBaseDataDialog`.
 */
export function CreateSampleBaseDataDialog({
  mutation,
}: {
  mutation: CreateSampleBaseDataMutation;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [create, { isLoading, error, reset }] = mutation;

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setForm(emptyForm);
      reset();
    }
  };

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    create({ id: v4(), label: form.label, companyId: form.companyId })
      .unwrap()
      .then(() => {
        toast.success(t("Basisdaten erstellt"));
        onOpenChange(false);
      })
      // The error is rendered from the mutation state below.
      .catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CommonDialogTrigger icon={Plus} label={t("Basisdaten")} />
      <DialogContent>
        <CommonDialogHeader
          title={t("Basisdaten erstellen")}
          description={t(
            "Die Bezeichnung unterscheidet die Basisdaten eines Unternehmens voneinander. Die Vorgabewerte werden anschließend erfasst.",
          )}
        />
        <div className="space-y-4">
          <div className="grid content-start gap-1.5">
            <Label>{t("Bezeichnung")}</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            <InputValidationErrors error={error} field="label" />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("Unternehmen")}</Label>
            <CompanySelect
              companyId={form.companyId}
              onCompanyIdChange={(companyId) => setForm({ ...form, companyId })}
            />
            <InputValidationErrors error={error} field="companyId" />
          </div>
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}
